import { Button, Flex } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import type { FormEvent } from "react";
import { useState } from "react";
import { useTokensValues } from "../../../../../../hooks/tokens";
import { paymentToggleAtom } from "../../../../../../store";
import type { Quote } from "../../../../types";
import { QuoteUnavailableForPurchase } from "../../../quoteUnavailableForPurchaseModal";
import { ReferredQuotesCallDisplay } from "../ReferredQuotesCallDisplay";

type SubmissionFormProps = {
  quote: Quote;
  hasInstallments: boolean;
};

type FormInputName =
  | "quote_id"
  | "insurer"
  | "payment_period"
  | "buy_button_clicked"
  | "rfq_id"
  | "authenticity_token"
  | "journey_token";

export type HiddenInput = {
  id: FormInputName;
  name: FormInputName;
  value?: string;
};

export const SubmissionForm = ({
  quote,
  hasInstallments,
}: SubmissionFormProps) => {
  const { snowplow } = useSnowplowContext();
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const paymentPeriod = useAtomValue(paymentToggleAtom);
  const tokens = useTokensValues();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    setIsPending(true);
    snowplow?.trigger("nextStepsClicked");
    try {
      event.currentTarget.submit();
      setIsPending(false);
      setIsSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(JSON.stringify({ error }));
      }
    }
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleButtonClick = () => {
    if (quote.quoteAvailableForPurchase === false) {
      openModal();
    } else {
      (
        document.getElementById(`form-${quote.id}`) as HTMLFormElement
      )?.submit();
    }
  };

  const actionsButtonClasses = classNames(
    "mobius-quote-actions__button",
    // `qa-buy-quote-option` class is used in cypress tests
    // keeping it to comply with shakedowns
    "qa-buy-quote-option",
    "mobius-quote-actions__link-button",
  );

  const hiddenInputs: HiddenInput[] = [
    {
      id: "authenticity_token",
      name: "authenticity_token",
      value: tokens?.authenticityToken || "",
    },
    {
      id: "journey_token",
      name: "journey_token",
      value: tokens?.journeyToken || "",
    },
    {
      id: "quote_id",
      name: "quote_id",
      value: quote.id,
    },
    {
      id: "insurer",
      name: "insurer",
      value: quote.covers[0]?.coverInsurer,
    },
    {
      id: "payment_period",
      name: "payment_period",
      value: hasInstallments ? paymentPeriod : "annual",
    },
    {
      id: "buy_button_clicked",
      name: "buy_button_clicked",
      value: "yes",
    },
    {
      id: "rfq_id",
      name: "rfq_id",
      value: quote.rfqId,
    },
  ];

  return (
    <>
      <form
        id={`form-${quote.id}`}
        method="POST"
        onSubmit={handleSubmit}
        data-testid={`form-${quote.id}`}
      >
        {hiddenInputs.map(el => (
          <input
            data-testid={el.id}
            id={el.id}
            type="hidden"
            value={el.value}
            key={el.id}
            name={el.name}
            onChange={() => {}}
          />
        ))}

        <Flex
          className="mobius-quote-actions"
          justifyContent="space-between"
          gap="sm"
        >
          {quote.offlinePurchaseOnly ? (
            <ReferredQuotesCallDisplay quote={quote} displayVariant="qds" />
          ) : (
            <Button
              id={`select-${quote.id}`}
              className={actionsButtonClasses}
              size="sm"
              type="button"
              isDisabled={isPending || isSuccess}
              onClick={() => handleButtonClick()}
            >
              {isPending || isSuccess ? "Please wait..." : "Next step"}
            </Button>
          )}
        </Flex>
      </form>
      <QuoteUnavailableForPurchase
        modalOpen={modalOpen}
        onClose={closeModal}
        insurerName={quote.covers[0]?.coverInsurer}
      />
    </>
  );
};
